import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Home,
  BookOpen,
  PlusCircle,
  Settings,
  BarChart3,
  Users,
  Palette,
  FileText,
  Crown,
  Shield,
  CreditCard,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  planCode?: string;
  role?: string;
}

interface SidebarProps {
  isCollapsed?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isCollapsed = false, isMobile = false, onClose }: SidebarProps) {
  const location = useLocation();
  
  // Get user data to show admin link if needed
  const { data: userData } = useQuery<{ user: UserData }>({
    queryKey: ["/api/auth/me"],
  });
  
  const user = userData?.user;

  const navItems = [
    {
      label: "Home",
      to: "/dashboard",
      icon: Home,
      active: location.pathname === "/dashboard" || location.pathname === "/",
    },
    {
      label: "Projects",
      to: "/my-stories",
      icon: BookOpen,
      active: location.pathname === "/my-stories",
    },
    {
      label: "Templates",
      to: "/templates",
      icon: FileText,
      active: location.pathname === "/templates",
    },
    {
      label: "Analytics",
      to: "/analytics",
      icon: BarChart3,
      active: location.pathname === "/analytics",
    },
    {
      label: "Billing",
      to: "/billing",
      icon: CreditCard,
      active: location.pathname === "/billing",
    },
    {
      label: "Upgrade Plan",
      to: "/upgrade",
      icon: Crown,
      active: location.pathname === "/upgrade",
    },
  ];

  // Add admin item if user is admin
  if (user?.role === "admin") {
    navItems.push({
      label: "Admin Panel",
      to: "/admin",
      icon: Shield,
      active: location.pathname === "/admin",
    });
  }

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <div className={cn(
      "flex flex-col h-full bg-card border-r border-border",
      isCollapsed ? "w-16" : "w-64",
      isMobile ? "w-80" : ""
    )}>
      {/* Logo Section */}
      <div className={cn(
        "p-4 border-b border-border",
        isMobile ? "p-6" : "p-6"
      )}>
        <Link to="/dashboard" onClick={handleLinkClick}>
          <div className="flex items-center gap-3 cursor-pointer">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Palette className="w-5 h-5 text-primary-foreground" />
            </div>
            {(!isCollapsed || isMobile) && (
              <div>
                <h1 className="font-bold text-lg">StoryLikho</h1>
                <p className="text-xs text-muted-foreground">AI Story Platform</p>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className={cn(
        "flex-1 p-4 space-y-2",
        isMobile ? "px-6" : "p-4"
      )}>
        {navItems.map((item) => (
          <Link key={item.to} to={item.to} onClick={handleLinkClick}>
            <Button
              variant={item.active ? "default" : "ghost"}
              className={cn(
                "w-full justify-start gap-3 h-12",
                isCollapsed && !isMobile && "px-3",
                item.active && "bg-primary text-primary-foreground",
                isMobile && "text-base py-3"
              )}
            >
              <item.icon className={cn(
                "w-5 h-5",
                isMobile && "w-6 h-6"
              )} />
              {(!isCollapsed || isMobile) && <span>{item.label}</span>}
            </Button>
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border space-y-2">
        <Link to="/profile">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3",
              isCollapsed && "px-3",
              location.pathname === "/profile" && "bg-primary text-primary-foreground"
            )}
          >
            <Users className="w-5 h-5" />
            {!isCollapsed && <span>Profile</span>}
          </Button>
        </Link>
        <Link to="/settings">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-3",
              isCollapsed && "px-3",
              location.pathname === "/settings" && "bg-primary text-primary-foreground"
            )}
          >
            <Settings className="w-5 h-5" />
            {!isCollapsed && <span>Settings</span>}
          </Button>
        </Link>
      </div>
    </div>
  );
}
